import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Loader2, AlertCircle, Share2 } from 'lucide-react';
import { Drawer, DateRangePicker } from '../ui';
import useGlobalCustomerStore from '../../stores/globalCustomerStore';
import useSocialMediaStore from '../../stores/socialMediaStore';
import usePermissionsStore from '../../stores/permissionsStore';
import { useToast } from '../../contexts/ToastContext';
import { useNavigationTracking } from '../../contexts/AnalyticsContext';
import { ReportType } from '../../types/report';
import DynamicParameterField from './DynamicParameterField';

interface GenerateReportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  reportTitle: string;
  reportType: string;
  reportTypeDefinition?: ReportType;
  onGenerate: (config: ReportGenerationConfig) => Promise<void>;
  onSaveShare?: (config: ReportGenerationConfig) => void | Promise<void>; // freeze a snapshot + go to the Share step
}

export interface ReportGenerationConfig {
  customerId: string;
  reportType: string;
  period: 'day' | 'week' | 'month' | '60days' | '90days' | 'custom';
  customStartDate?: string;
  customEndDate?: string;
  accountIds?: string[];
  postTypes?: string[];
  [key: string]: any; // Allow dynamic parameters
}

const GenerateReportDrawer: React.FC<GenerateReportDrawerProps> = ({
  isOpen,
  onClose,
  reportTitle,
  reportType,
  reportTypeDefinition,
  onGenerate,
  onSaveShare
}) => {
  const { showError } = useToast();
  const { trackDrawerOpened } = useNavigationTracking();
  const { permissions } = usePermissionsStore();
  const { selectedCustomer, customerAccounts } = useGlobalCustomerStore();
  const accounts = selectedCustomer ? customerAccounts[selectedCustomer.id] || [] : [];
  const {
    currentCustomerId,
    availablePostTypes,
    loadAvailablePostTypes,
    setCustomerId
  } = useSocialMediaStore();

  // Local state for filters
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | '60days' | '90days' | 'custom'>('month');
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string } | null>(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedPostType, setSelectedPostType] = useState<string>('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Dynamic parameters state
  const [dynamicParams, setDynamicParams] = useState<Record<string, any>>({});

  // Initialize dynamic parameters with default values
  useEffect(() => {
    if (isOpen && reportTypeDefinition?.metadata.parameters) {
      const initialParams: Record<string, any> = {};
      Object.entries(reportTypeDefinition.metadata.parameters).forEach(([key, def]) => {
        if (def.defaultValue !== undefined) {
          initialParams[key] = def.defaultValue;
        }
      });
      setDynamicParams(initialParams);
    }
  }, [isOpen, reportTypeDefinition]);

  // Reset form to defaults
  const resetForm = () => {
    setPeriod('month');
    setCustomDateRange(null);
    setSelectedAccountIds(accounts.map(a => a.id)); // Default to all accounts
    setSelectedPostType('all');
    setDynamicParams({});
  };

  // Handle close with reset
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Track drawer opened
  useEffect(() => {
    if (isOpen) {
      trackDrawerOpened('generate_report', { reportTitle });
    }
  }, [isOpen, reportTitle, trackDrawerOpened]);

  // Sync customer ID from global store when drawer opens
  useEffect(() => {
    if (isOpen && selectedCustomer?.id && selectedCustomer.id !== currentCustomerId) {
      setCustomerId(selectedCustomer.id);
    }
  }, [isOpen, selectedCustomer?.id, currentCustomerId, setCustomerId]);

  // Load available filters and reset form when drawer opens
  useEffect(() => {
    if (isOpen && currentCustomerId) {
      loadAvailablePostTypes();
    }
  }, [isOpen, currentCustomerId]);

  // Set all accounts selected by default when accounts change or drawer opens
  useEffect(() => {
    if (isOpen && accounts.length > 0) {
      setSelectedAccountIds(accounts.map(a => a.id));
    }
  }, [isOpen, accounts.length]);

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    // Customer must be selected
    if (!currentCustomerId) {
      return false;
    }

    // Check required dynamic parameters
    if (reportTypeDefinition?.metadata.parameters) {
      const hasAllRequiredParams = Object.entries(reportTypeDefinition.metadata.parameters).every(([key, def]) => {
        if (!def.required) {
          return true; // Optional fields don't affect validity
        }

        const value = dynamicParams[key];

        // Check if value exists and is not empty
        if (value === undefined || value === null || value === '') {
          return false;
        }

        // For arrays (multiselect), check length
        if (Array.isArray(value) && value.length === 0) {
          return false;
        }

        return true;
      });

      return hasAllRequiredParams;
    }

    // No required parameters, form is valid if customer is selected
    return true;
  }, [currentCustomerId, reportTypeDefinition, dynamicParams]);

  // Validate customer + required params, then build the generation config. Shared by the
  // "Generate Report" (PDF) and "Save & Share" (snapshot) actions.
  const buildValidatedConfig = (): ReportGenerationConfig | null => {
    if (!currentCustomerId || !selectedCustomer) {
      showError('No customer selected', 'Please select a customer to generate a report.');
      return null;
    }
    if (reportTypeDefinition?.metadata.parameters) {
      const missingParams: string[] = [];
      Object.entries(reportTypeDefinition.metadata.parameters).forEach(([key, def]) => {
        if (def.required && !dynamicParams[key]) missingParams.push(def.label);
      });
      if (missingParams.length > 0) {
        showError('Missing required fields', `Please provide: ${missingParams.join(', ')}`);
        return null;
      }
    }
    return {
      customerId: currentCustomerId,
      reportType: reportType,
      period: period,
      customStartDate: customDateRange?.start,
      customEndDate: customDateRange?.end,
      accountIds: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
      postTypes: selectedPostType && selectedPostType !== 'all' ? [selectedPostType] : undefined,
      ...dynamicParams, // Spread dynamic parameters into config
    };
  };

  const handleGenerate = async () => {
    const config = buildValidatedConfig();
    if (!config) return;
    setIsGenerating(true);
    try {
      await onGenerate(config);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsGenerating(false);
    }
  };

  // Save & Share: hand the validated config to the parent, which freezes a snapshot and
  // opens the Share step (recipients / expiry / password). Keep this drawer open with a
  // spinner while the snapshot is generated (2-3s) — the parent closes it on success.
  const handleSaveShare = async () => {
    const config = buildValidatedConfig();
    if (!config) return;
    setIsSaving(true);
    try {
      await onSaveShare?.(config);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title={`Generate ${reportTitle}`}
      description="Configure your report parameters"
      icon={<FileText className="h-5 w-5" />}
      width="lg"
    >
      <div className="space-y-6">
        {/* No Customer Selected Warning */}
        {!selectedCustomer && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">No customer selected</p>
              <p className="text-xs text-amber-600 mt-1">Please select a customer from the header to generate a report.</p>
            </div>
          </div>
        )}

        {/* Period Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Period
          </label>
          <DateRangePicker
            value={customDateRange}
            selectedPeriod={period}
            onChange={(range, selectedPeriod) => {
              if (selectedPeriod === 'custom' && range) {
                setCustomDateRange(range);
                setPeriod('custom');
              } else {
                setPeriod(selectedPeriod as any);
                setCustomDateRange(null);
              }
            }}
            maxRetentionDays={permissions?.effectiveLimits?.post_retention_days || 90}
            disabled={!currentCustomerId}
          />
        </div>

        {/* Account Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Accounts
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {/* All Accounts option */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedAccountIds.length === accounts.length && accounts.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Select all accounts
                    setSelectedAccountIds(accounts.map(a => a.id));
                  } else {
                    // Deselect all accounts
                    setSelectedAccountIds([]);
                  }
                }}
                disabled={!currentCustomerId}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs font-medium text-gray-700">
                All Accounts ({accounts.length})
              </span>
            </label>

            {accounts.length > 0 && (
              <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                {accounts.map((account) => (
                  <label key={account.id} className="flex items-center space-x-2 cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={selectedAccountIds.includes(account.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAccountIds([...selectedAccountIds, account.id]);
                        } else {
                          setSelectedAccountIds(selectedAccountIds.filter(id => id !== account.id));
                        }
                      }}
                      disabled={!currentCustomerId}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-gray-700 block truncate">
                        {account.displayName || account.username}
                      </span>
                      <span className="text-xs text-gray-500">
                        @{account.username} • {account.platformDisplayName}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Post Type Selection */}
        <div>
          <label htmlFor="posttype-select" className="block text-xs font-medium text-gray-700 mb-2">
            Post Type
          </label>
          <select
            id="posttype-select"
            value={selectedPostType}
            onChange={(e) => setSelectedPostType(e.target.value)}
            disabled={!currentCustomerId}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          >
            <option value="all">All Post Types</option>
            {availablePostTypes.map((postType: any) => (
              <option key={postType.id} value={postType.typeName}>
                {postType.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Dynamic Parameters */}
        {reportTypeDefinition?.metadata.parameters && (
          <>
            {Object.keys(reportTypeDefinition.metadata.parameters).length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-xs font-semibold text-gray-700 mb-3">Report Parameters</h3>
                <div className="space-y-4">
                  {Object.entries(reportTypeDefinition.metadata.parameters).map(([key, definition]) => (
                    <DynamicParameterField
                      key={key}
                      paramKey={key}
                      definition={definition}
                      value={dynamicParams[key]}
                      onChange={(value) => setDynamicParams(prev => ({ ...prev, [key]: value }))}
                      disabled={!currentCustomerId}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={isGenerating || isSaving}
            className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={!isFormValid || isGenerating || isSaving}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                Download PDF
              </>
            )}
          </button>
          {onSaveShare && (
            <button
              onClick={handleSaveShare}
              disabled={!isFormValid || isGenerating || isSaving}
              title="Freeze a snapshot and share a link"
              className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white text-xs font-medium rounded-lg hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Share2 className="h-3.5 w-3.5 mr-1.5" />
                  Save &amp; Share
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default GenerateReportDrawer;
